/**
 * React Router 6 Route Configuration
 *
 * This file defines all application routes using the RR6 data router format.
 * Route guards are implemented using wrapper components instead of onEnter hooks.
 */

import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import {
  RequireAuth,
  RequireNoAuth,
  RequireSubscribed,
  RequireNotSubscribed,
  RequireOrderReady,
  RequireNotLibrarySubscriber,
  RequireNotLifetime,
  RequireGiftSubscription
} from './components/RouteGuards'

import App from './components/App'
import ArtistList from './components/Artists/ArtistList'
import ArtistDetail from './components/Artists/ArtistDetail'
import LandingPage from './components/LandingPage'
import CourseCategoryLandingPage from './components/CourseCategoryLandingPage'
import Page from './components/Page'
import EmailVerify from './components/EmailVerify'
import Blog from './components/Blog/_lazy'
import Verify from './components/Verify/_lazy'
import Gallery from './components/Gallery'
import Post from './components/Blog/Post/_lazy'
import ForgotPassword from './components/Auth/ForgotPassword'
import ResetPassword from './components/Auth/ResetPassword'
import ChangePassword from './components/Auth/ChangePassword'
import Home from './components/Home'
import Courses from './components/Account/Courses'
import SignUp from './components/Auth/SignUp'
import SignIn from './components/Auth/SignIn'
import OtpLogin from './components/Auth/OtpLogin'
import PreSignUp from './components/Auth/PreSignUp'
import Contact from './components/Contact'
import Favorites from './components/Account/Favorites/FavoritesList'
import WebinarDetail from './components/Webinars/WebinarDetail/_lazy'
import TutorialList from './components/Tutorials/TutorialList'
import TutorialDetail from './components/Tutorials/TutorialDetail/_lazy'
import TutorialRedirect from './components/TutorialRedirect'
import PathwayRedirect from './components/PathwayRedirect'
import Team from './components/Team'
import TeamMember from './components/Team/TeamMember'
import Addresses from './components/Account/Addresses/_lazy'
import PaymentMethods from './components/Account/PaymentMethods'
import NewSearch from './components/NewSearch/index'
import Cart from './components/Cart'
import Checkout from './components/Checkout/_lazy'
import Account from './components/Account'
import CustomerCare from './components/CustomerCare'
import SubscriptionPlans from './components/SubscriptionPlans/index'
import ReviewOrder from './components/ReviewOrder'
import NotFound from './components/Static/NotFound'
import MyAccount from './components/Account/MyAccount'
import WatchHistory from './components/Account/WatchHistory'
import Orders from './components/Account/Orders'
import GiftSubscription from './components/Account/GiftSubscription'
import Interests from './components/Account/Interests'
import Receipt from './components/Receipt'
import ThankYou from './components/ThankYou'
import ChangeSubscription from './components/Account/ChangeSubscription'

const ChangeSubscriptionRoute = () => {
  const location = useLocation()
  return <ChangeSubscription key={location.search || 'no-coupon'} />
}
import SpecialVideoDetail from './components/SpecialVideos/SpecialVideoDetail'
import SpecialVideoPlayer from './components/SpecialVideos/SpecialVideoPlayer'
import Hijack from './components/Hijack'
import Contest from './components/Contest'
import BookmarkList from './components/Account/BookmarkList'
import LearningPathDetail from './components/LearningPaths/LearningPathDetail'
import LearningPathList from './components/LearningPaths/LearningPathList'
import LearningPaths from './components/Account/LearningPaths'
import Certificates from './components/Account/Certificates'
import LeadMagnetPage from './components/LeadMagnetPage'
import TestimonalList from './components/TestimonialList'
import ProxySSO from './components/ProxySSO'
import OpenAthensSSO from './components/OpenAthensSSO'
import OpenAthensSSOError from './components/OpenAthensSSOError'
import AboutUs from './components/AboutUs'
import WalkthroughVideo from './components/WalkthroughVideo'

/**
 * Route configuration for React Router 6 data router
 */
const routes = [
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <Home /> },
      { path: 'artists', element: <ArtistList /> },
      { path: 'artists/:artistSlug', element: <ArtistDetail /> },
      { path: 'blog', element: <Blog /> },
      { path: 'blog/:postSlug', element: <Post /> },
      { path: 'stan-winston-school-of-character-arts', element: <AboutUs /> },
      { path: 'confirm-email', element: <EmailVerify /> },
      { path: 'users/password/new', element: <ForgotPassword /> },
      { path: 'password/reset/:uid/:token', element: <ResetPassword /> },
      { path: 'gift-certificate/:token', element: <PreSignUp /> },
      // Public certificate verification (SPEC-040): reached from the QR code
      // printed on every certificate, so it must work logged out.
      { path: 'verify', element: <Verify /> },
      { path: 'verify/:code', element: <Verify /> },
      {
        path: 'account/change_password',
        element: (
          <RequireAuth>
            <ChangePassword />
          </RequireAuth>
        )
      },
      {
        path: 'users/signup',
        element: (
          <RequireNoAuth>
            <SignUp />
          </RequireNoAuth>
        )
      },
      {
        path: 'login',
        element: (
          <RequireNoAuth>
            <SignIn />
          </RequireNoAuth>
        )
      },
      {
        path: 'login/otp',
        element: (
          <RequireNoAuth>
            <OtpLogin />
          </RequireNoAuth>
        )
      },
      { path: 'landing_pages/:slug', element: <LandingPage /> },
      { path: 'lm/:slug', element: <LeadMagnetPage /> },
      // Redirects
      { path: 'signup', element: <Navigate to="/users/signup" replace /> },
      { path: 'register', element: <Navigate to="/users/signup" replace /> },
      { path: 'webcourses', element: <Navigate to="/tutorials" replace /> },
      { path: 'videos', element: <Navigate to="/tutorials" replace /> },
      {
        path: 'videos/:tutorialSlug',
        element: <TutorialRedirect />
      },
      {
        path: 'videos/:tutorialSlug/watch',
        element: <TutorialRedirect watch />
      },
      { path: 'webcourses/:webinarSlug', element: <WebinarDetail /> },
      {
        path: 'tutorial_categories/:slug',
        element: <CourseCategoryLandingPage />
      },
      { path: 'tutorials', element: <TutorialList /> },
      { path: 'tutorials/:tutorialSlug', element: <TutorialDetail /> },
      {
        path: 'tutorials/:tutorialSlug/watch',
        element: <TutorialRedirect watch />
      },
      { path: 'gallery', element: <Gallery /> },
      { path: 'search', element: <NewSearch /> },
      {
        path: 'cart/:token?',
        element: (
          <RequireNotLibrarySubscriber>
            <Cart />
          </RequireNotLibrarySubscriber>
        )
      },
      {
        path: 'checkout',
        element: (
          <RequireNotLifetime>
            <RequireNotLibrarySubscriber>
              <Checkout />
            </RequireNotLibrarySubscriber>
          </RequireNotLifetime>
        )
      },
      // Account section with nested routes
      {
        path: 'account',
        element: (
          <RequireAuth>
            <Account />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <MyAccount /> },
          // The course-completion email links to /account/certificates
          { path: 'certificates', element: <Certificates /> }
        ]
      },
      // Account routes that need their own paths
      {
        path: 'history',
        element: (
          <RequireAuth>
            <Account>
              <WatchHistory />
            </Account>
          </RequireAuth>
        )
      },
      {
        path: 'courses',
        element: (
          <RequireAuth>
            <Account>
              <Courses />
            </Account>
          </RequireAuth>
        )
      },
      {
        path: 'favorites',
        element: (
          <RequireAuth>
            <Account>
              <Favorites />
            </Account>
          </RequireAuth>
        )
      },
      {
        path: 'addresses',
        element: (
          <RequireAuth>
            <Account>
              <Addresses />
            </Account>
          </RequireAuth>
        )
      },
      {
        path: 'credit_cards',
        element: (
          <RequireAuth>
            <Account>
              <PaymentMethods />
            </Account>
          </RequireAuth>
        )
      },
      {
        path: 'orders',
        element: (
          <RequireAuth>
            <Account>
              <Orders />
            </Account>
          </RequireAuth>
        )
      },
      {
        path: 'change_subscription',
        element: (
          <RequireSubscribed>
            <RequireNotLifetime>
              <Account>
                <ChangeSubscriptionRoute />
              </Account>
            </RequireNotLifetime>
          </RequireSubscribed>
        )
      },
      {
        path: 'bookmarks',
        element: (
          <RequireAuth>
            <Account>
              <BookmarkList />
            </Account>
          </RequireAuth>
        )
      },
      {
        path: 'my_pathways',
        element: (
          <RequireAuth>
            <Account>
              <LearningPaths />
            </Account>
          </RequireAuth>
        )
      },
      {
        // The course-completion email CTA links here with ?course=<slug>,
        // which opens the claim dialog over the My Certificates list.
        path: 'certificates',
        element: (
          <RequireAuth>
            <Account>
              <Certificates />
            </Account>
          </RequireAuth>
        )
      },
      {
        path: 'gift_subscription',
        element: (
          <RequireGiftSubscription>
            <Account>
              <GiftSubscription />
            </Account>
          </RequireGiftSubscription>
        )
      },
      {
        path: 'interests',
        element: (
          <RequireAuth>
            <Account>
              <Interests />
            </Account>
          </RequireAuth>
        )
      },
      // Customer care section with nested routes
      {
        path: 'customer-care',
        element: <CustomerCare />,
        children: [
          { index: true, element: <Contact /> },
          { path: 'contact-us', element: <Contact /> },
          { path: ':slug', element: <Page /> }
        ]
      },
      {
        path: 'subscriptions/plans',
        element: (
          <RequireNotSubscribed>
            <SubscriptionPlans />
          </RequireNotSubscribed>
        )
      },
      {
        path: 'subscription/confirmation',
        element: (
          <RequireSubscribed>
            <ThankYou />
          </RequireSubscribed>
        )
      },
      {
        path: 'review_order',
        element: (
          <RequireOrderReady>
            <ReviewOrder />
          </RequireOrderReady>
        )
      },
      {
        path: 'receipt',
        element: <Receipt />
      },
      { path: 'the-team', element: <Team /> },
      { path: 'the-team/:memberSlug', element: <TeamMember /> },
      {
        path: 'special-videos/:specialVideoSlug',
        element: <SpecialVideoDetail />
      },
      {
        path: 'special-videos/:specialVideoSlug/watch',
        element: (
          <RequireAuth>
            <SpecialVideoPlayer />
          </RequireAuth>
        )
      },
      {
        path: 'hijack',
        element: (
          <RequireAuth>
            <Hijack />
          </RequireAuth>
        )
      },
      { path: 'contest/:slug', element: <Contest /> },
      { path: 'pathways/:learningPathSlug', element: <LearningPathDetail /> },
      { path: 'pathways', element: <LearningPathList /> },
      {
        path: 'learning_pathways/:slug',
        element: <PathwayRedirect />
      },
      { path: 'testimonials', element: <TestimonalList /> },
      { path: 'contest/:slug/thank-you', element: <ThankYou /> },
      { path: 'proxy-sso', element: <ProxySSO /> },
      { path: 'open-athens-redirect', element: <OpenAthensSSO /> },
      { path: 'walkthrough', element: <WalkthroughVideo /> },
      {
        path: 'open-athens-redirect-auth-error',
        element: <OpenAthensSSOError />
      },
      { path: '404', element: <NotFound /> },
      { path: '*', element: <NotFound /> }
    ]
  }
]

export default routes
